// src/components/exam-player/renderers/ImageMatchingRenderer.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CDN_BASE_URL } from "../../../config";

function shuffleArray(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

export default function ImageMatchingRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const body = item?.body || {};
  const pairs = Array.isArray(body.pairs) ? body.pairs : [];
  const layout = body.layout || "left-right";
  const shuffle = !!body.shuffle;

  const isVertical = layout === "top-bottom" || layout === "bottom-top";
  const imagesFirst = layout === "left-right" || layout === "top-bottom";

  // آماده‌سازی آرایه تصاویر و لیبل‌ها
  const imageItems = useMemo(
    () =>
      pairs.map((p, idx) => {
        const url = p?.image?.url || "";
        const alt =
          p?.image?.alt?.fa ||
          p?.image?.alt?.en ||
          p?.label?.fa ||
          p?.label?.en ||
          `#${idx + 1}`;
        return { idx, url, alt };
      }),
    [pairs]
  );

  const labelItems = useMemo(
    () =>
      pairs.map((p, idx) => {
        const label =
          p?.label?.fa ||
          p?.label?.en ||
          p?.image?.alt?.fa ||
          p?.image?.alt?.en ||
          `#${idx + 1}`;
        return { idx, label };
      }),
    [pairs]
  );

  const renderedLabels = useMemo(
    () => (shuffle ? shuffleArray(labelItems) : labelItems),
    [labelItems, shuffle]
  );

  // مقدار اولیه از initialAnswers
  const initialMap = useMemo(() => {
    if (!initialAnswers || typeof initialAnswers !== "object") return {};
    if (initialAnswers.matches && typeof initialAnswers.matches === "object") {
      return { ...initialAnswers.matches };
    }
    return { ...initialAnswers };
  }, [initialAnswers]);

  const [matches, setMatches] = useState(initialMap);

  useEffect(() => {
    setMatches(initialMap);
  }, [initialMap]);

  const handleSelect = (imgIdxStr, labelIdxStr) => {
    const next = {
      ...matches,
      [imgIdxStr]: labelIdxStr || undefined,
    };
    setMatches(next);
    onAnswerChange?.(next); // map را به بالا می‌فرستیم
  };

  if (!pairs.length) {
    return (
      <div className="text-sm text-red-600">
        {t("preview.kind_not_supported", { kind: "image_matching" })}
      </div>
    );
  }

  const stemHtml =
    item?.stem?.fa || item?.stem?.en || t("preview.no_stem", "بدون متن");

  const containerClass = isVertical
    ? "grid grid-cols-1 gap-4 mt-2"
    : "grid grid-cols-1 md:grid-cols-2 gap-4 mt-2";

  const imagesColumn = (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-gray-500">
        {t("image_matching.images_col", "تصاویر")}
      </div>
      {imageItems.map((img) => (
        <div
          key={img.idx}
          className="flex items-center gap-3 border rounded-lg p-2 bg-white"
        >
          {img.url ? (
            <img
              src={img.url?.replace("//u","/u")}
              alt={img.alt}
              className="w-24 h-24 object-cover rounded"
            />
          ) : (
            <div className="w-24 h-24 bg-gray-200 rounded flex items-center justify-center text-[10px] text-gray-500">
              {t("image_matching.no_image", "بدون تصویر")}
            </div>
          )}
          {/* <div className="text-xs text-gray-600 flex-1">{img.alt}</div> */}
        </div>
      ))}
    </div>
  );

  const selectsColumn = (
    <div className="space-y-3">
      <div className="text-xs font-semibold text-gray-500">
        {t("image_matching.labels_col", "برچسب‌ها")}
      </div>
      {imageItems.map((img) => {
        const key = String(img.idx);
        const value = matches[key] ?? "";
        return (
          <select
            key={img.idx}
            className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
            value={value}
            onChange={(e) => handleSelect(key, e.target.value || "")}
          >
            <option value="">
              {t("image_matching.select_placeholder", "انتخاب کنید")}
            </option>
            {renderedLabels.map((lbl) => (
              <option key={lbl.idx} value={String(lbl.idx)}>
                {lbl.label}
              </option>
            ))}
          </select>
        );
      })}
    </div>
  );

  return (
    <div dir={isRtl ? "rtl" : "ltr"}>
      {/* Stem */}
      <div className="prose prose-sm max-w-none mb-4">
        <div dangerouslySetInnerHTML={{ __html: stemHtml }} />
        {/* {typeof questionNumber === "number" && (
          <div className="text-xs text-gray-500 mt-1">
            {t("preview.question_number", { n: questionNumber })}
          </div>
        )} */}
      </div>

      {/* محتوا */}
      <div className={containerClass}>
        {imagesFirst ? (
          <>
            {imagesColumn}
            {selectsColumn}
          </>
        ) : (
          <>
            {selectsColumn}
            {imagesColumn}
          </>
        )}
      </div>
    </div>
  );
}
